import { Component, OnInit } from '@angular/core';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { DataService, DateService, FormatService, HelperService, HTTPService, ValidationService } from 'app/services';
import { config, paymentTypes, payrixStatus } from 'app/constants';
import { datetimeFormat } from 'app/constants/date-time-format';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components/confirm-dismiss-dialog/confirm-dismiss-dialog.component';
import { currency } from 'app/constants/currency.constants';

import { concat, lowerCase, orderBy } from 'lodash';
import { SwipeCardConfirmationComponent } from '../payment-add/swipe-card-confirmation/swipe-card-confirmation.component';
import { GlobalPaymentFieldComponent } from '../payment-add/global-payment-field/global-payment-field.component';

@Component({
  selector: 'app-refund-payment-dialog',
  templateUrl: './refund-payment-dialog.component.html',
  styleUrls: ['./refund-payment-dialog.component.scss'],
  providers: [DialogService],
})
export class RefundPaymentDialogComponent implements OnInit {
  // required input ids
  refundId: string = '';
  paymentId: string = '';
  estimateId: string = '';
  refundStatus: number;
  voidRefundAmount: string = '';
  voidPayment = false;
  title = '';
  confirmStatus = false;
  type: string = '';
  refundType = {
    void: '4',
    refund: '5',
  };
  featureIsPayRixEnable: boolean;
  // pre-req data
  defaultOrderTitle: string = config.defaultTitle;
  usd: string = currency.USD;
  paymentTypes: any[] = paymentTypes;
  paymentMethods: any[] = [];
  maxDateRange: any = new Date();

  // state flags
  isPaymentArchived: boolean;
  isLoadingPayment: boolean = false;
  isLoadingRefund: boolean = false;
  isLoadingPaymentMethods: boolean = false;
  isSubmitting: boolean = false;
  isRefundAmountFieldFocused: boolean = false;

  // form state
  activePaymentType: any = this.paymentTypes[0];
  selectedPaymentMethod: any = {};
  payment = {
    // estimateId: '',
    title: '',
    code: '',
    paymentType: this.activePaymentType.value,
    isPaidFull: false,
    workFlowPaid: false,
    paymentDate: null,
    payRix: null,
    isOnline: false,
    isCardSwipe: false,
    cardType: '',
    card4Digits: '',
    cardConfNo: '',
    checkNo: '',
    refundToDate: 0,
    amount: 0,
    history: [],
    paymentProvider: '',
    terminalId: '',
    customer: {},
    id: '',
  };
  order = {
    estimateId: '',
    code: '',
    invCode: '',
    title: '',
    workFlowStatus: '',
    createdAt: null,
    estimateBy: '',
  };
  customer = {
    customerId: '',
    fullName: '',
    email: '',
    phone: '',
  };
  refund = {
    amount: 0,
    originalAmount: 0,
    notes: '',
    refundDate: null,
    refundTime: null,
    refundDateTime: '',
    collectedAmount: 0,
    card4Digits: '',
    cardConfNo: '',
  };
  errors = {
    refundAmountError: '',
  };

  // GP payment refund
  isSourcePaymentOnline: boolean = false;
  isSourcePaymentCardSwipe: boolean = false;
  terminalList: any[] = [];
  selectedTerminal: any;
  opening: boolean = false;
  credentials: any = {};
  paymentTime: any = '';
  afterRefundOnline = false;
  isTransectionStatusDebit = false;
  private transactionDialogue: DynamicDialogRef;
  // void and refund type
  refundTitle = 'Refund';
  isSwipeRequestPending: boolean = false;
  swapAmount = 0;
  changePaymentData: boolean = false;
  isPaymentDataPrivilgesEnabled: boolean = false;
  constructor(
    public dialogConfig: DynamicDialogConfig,
    private httpService: HTTPService,
    public helperService: HelperService,
    public validationService: ValidationService,
    public dataService: DataService,
    public dateService: DateService,
    public ref: DynamicDialogRef,
    public dialogService: DialogService,
    private formatService: FormatService
  ) {}

  ngOnInit() {
    this.featureIsPayRixEnable = this.dataService.isOnlinePaymentEnable || this.dataService.isGlobalPayEnabled;
    const { paymentId = undefined, refundId = undefined, refundStatus = undefined, voidRefundAmount = undefined, isTransectionStatusDebit } = this.dialogConfig.data || {};
    this.paymentId = paymentId;
    this.refundId = refundId;
    this.isTransectionStatusDebit = isTransectionStatusDebit;
    this.refundStatus = refundStatus;
    this.voidRefundAmount = voidRefundAmount;
    if (this.refundStatus === payrixStatus['Approved']) {
      this.voidPayment = true;
      this.type = this.refundType.void;
    } else if (this.refundStatus === payrixStatus['Captured'] || payrixStatus['Refund']) {
      this.type = this.refundType.refund;
    }

    if (this.refundStatus === payrixStatus['Approved'] && !this.refundId) {
      this.title = 'Void';
      this.refundTitle = 'Void';
    } else if (this.refundStatus !== payrixStatus['Approved'] && !this.refundId) {
      this.title = 'Add Refund';
    } else {
      this.title = 'Edit Refund';
    }

    // load all payment methods
    this.configureTimeAndDate();
    this.populatePaymentMethods();

    // get payment data
    this.getPaymentDetails();

    // get refund data if editing a refund
    if (this.refundId) {
      this.getRefundDetails();
    }

    this.getTerminals();
    this.getGlobalPaymentCredentials();
    this.changePaymentData = this.dataService.changePaymentData;
    this.isPaymentDataPrivilgesEnabled = this.dataService.isPaymentDataPrivilgesEnabled;
  }

  /**
   * Calculate payments remaining amount
   *
   * @returns
   */
  get remainingPaymentBalance(): number {
    return this.payment.amount - this.refundToDate;
  }

  /**
   * Get post refund amount
   *
   * @returns
   */
  get postRefundAmount(): number {
    return Number(Number(this.payment.amount - this.refundToDate - Number(this.refund.amount)).toFixed(2));
  }

  get refundToDate(): number {
    return this.refundId ? this.payment.refundToDate - this.refund.originalAmount : this.payment.refundToDate;
  }

  /**
   * Return customer name post-fixed with email if it exists
   */
  get customerName(): string {
    if (!this.customer.fullName) return null;

    let name = this.customer.fullName;

    if (this.customer.email) {
      name = `${name} - ${this.customer.email}`;
    }

    return name;
  }

  /**
   * Fetch payments and set the qboId for the non credit card payment
   */
  private populatePaymentMethods(): void {
    this.isLoadingPaymentMethods = true;
    this.httpService
      .getActivePaymentMethods()
      .then((paymentMethods: any[]) => {
        // store all methods and initally set all payment methods as visible
        this.paymentMethods = paymentMethods || [];
      })
      .catch((err) => this.helperService.showToaster('error', err))
      .finally(() => (this.isLoadingPaymentMethods = false));
  }

  /**
   * Configure dialog date time with server time
   */
  private configureTimeAndDate(): void {
    this.httpService.getReviewConfiguration().then(
      (configuration) => {
        const maxDateRange = this.dateService.getCurrentDateTimeTz(configuration.serverTime);
        this.maxDateRange = new Date(maxDateRange);

        if (!this.refundId || this.afterRefundOnline) {
          this.refund.refundDate = this.maxDateRange;
          this.refund.refundTime = this.maxDateRange;
        }
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  /**
   * Build history aginst a payment
   *
   * @param paymentHistory refund history array from payment
   * @returns
   */
  private buildHistory(paymentHistory: any[], payment?: any): any[] {
    if (!paymentHistory || !paymentHistory.length) return;
    const history: any[] = paymentHistory.map((history) => {
      const newHistory: any = {
        amount: history.amount || '',
      };
      if (history.paymentType) {
        newHistory.type = history.paymentMethod;
        newHistory.madeBy = history.fullName;
        newHistory.checkNo = payment.checkNo;
        newHistory.amount = history.amount;
        newHistory.isOnline = payment.isOnline;
        newHistory.isCardSwipe = payment.isCardSwipe;
        newHistory.paymentType = payment.paymentType;
        newHistory.cardType = payment.cardType;
        newHistory.card4Digits = payment.card4Digits;
        newHistory.cardSource = this.helperService.paymentToolTipTxt(payment, payment.paymentMethod);
        newHistory.parRix = payment.payrixResponse;
        newHistory.tooltip = this.helperService.createPaymentLogTooltip(payment);
        newHistory.date = history.createdAt ? this.dateService.getCurrentDateTimeTz(new Date(history.createdAt).getTime()) : null;
      } else if (history.refundDate) {
        newHistory.type = 'Refund';
        newHistory.madeBy = history.fullName;
        newHistory.title = payment.isVoidPayment ? 'Void' : 'Refund';
        newHistory.date = history.refundDate
          ? this.dateService.getCurrentDateTimeTz(new Date(history.refundDate).getTime())
          : this.dateService.getCurrentDateTimeTz(new Date(history.createdAt).getTime());
      }

      return newHistory;
    });

    return orderBy(history, ['date'], 'desc');
  }

  /**
   * Get payment details
   */
  private getPaymentDetails(): void {
    this.isLoadingPayment = true;
    this.httpService
      .getPaymentById(this.paymentId)
      .then((payment: any) => {
        // if no payment found don't do anything
        if (!payment) return;

        if (payment.isOnline || payment.isCardSwipe) {
          this.isSourcePaymentCardSwipe = true;
          // in case of any pending refund
          this.checkSwapPendingService(payment.estimate.estimateId._id);
        }
        // get order and customer object from payment
        const order = (payment.estimate && payment.estimate.estimateId) || payment.estimate || {};
        const customer = payment.customer || {};

        // check if payment is archived or not
        this.isPaymentArchived = order.isArchived;

        // set payment details
        this.payment = {
          customer,
          title: order.title || '',
          code: order.code || '',
          amount: payment.amount,
          paymentType: payment.paymentType,
          card4Digits: payment.card4Digits,
          cardConfNo: payment.cardConfNo,
          payRix: payment.payrixResponse,
          isOnline: payment.isOnline,
          isCardSwipe: payment.isCardSwipe,
          checkNo: payment.checkNo,
          isPaidFull: order.isPaidFull,
          workFlowPaid: !!(order.workFlowStatus && order.workFlowStatus.name === 'Paid'),
          cardType: '',
          paymentDate: new Date(payment.paymentDate),
          refundToDate: payment.refundToDate || 0,
          history: this.buildHistory(concat(payment.refunds || [], payment.paymentHistory || []), payment),
          paymentProvider: '',
          terminalId: '',
          id: payment._id,
        };
        // set payment if it is void

        // set customer details if found
        if (customer.customerId) {
          this.customer = {
            customerId: customer.customerId,
            fullName: customer.fullName,
            email: customer.email,
            phone: customer.phone,
          };
        }

        // set order detials if found
        if (order._id) {
          this.order = {
            estimateId: order._id,
            code: order.code,
            invCode: order.invCode,
            title: order.title,
            workFlowStatus: (order.workFlowStatus && order.workFlowStatus.name) || '',
            createdAt: this.dateService.getCurrentDateTimeTz(new Date(order.createdAt).getTime()),
            estimateBy: '',
          };
        }

        // get selected payment method id and set it on ui
        const paymentMethodId: string = payment.paymentMethod && payment.paymentMethod.methodId;
        if (paymentMethodId && this.paymentMethods.length) {
          // find selected payment method using id and set its object
          const [paymentMethod] = this.paymentMethods.filter(({ _id }) => _id === paymentMethodId);

          // selected selected method type
          const [activePaymentType] = this.paymentTypes.filter(({ value }) => this.payment.paymentType === value);

          // if both payment method and its type found set active payment method
          if (paymentMethod && activePaymentType) {
            this.activePaymentType = activePaymentType;
            this.selectedPaymentMethod = paymentMethod;
          }
        }

        setTimeout(() => {
          if (this.voidPayment) {
            this.refund.amount = this.postRefundAmount;
          }
        }, 1000);
      })
      .catch((err) => this.helperService.showToaster('error', err))
      .finally(() => (this.isLoadingPayment = false));
  }

  /**
   * Get refund details
   */
  private getRefundDetails(): void {
    this.isLoadingRefund = true;
    this.httpService
      .getRefund(this.refundId)
      .then((refundData: any) => {
        if (!refundData || !refundData.refunds) return;

        this.refund.notes = refundData.refunds.notes;
        this.refund.amount = refundData.refunds.amount || 0;
        this.refund.originalAmount = Number(refundData.refunds.amount || 0);
        this.refund.refundDate = this.dateService.getCurrentDateTimeTz(new Date(refundData.refunds.refundDate).getTime());
        this.refund.refundTime = this.dateService.getCurrentDateTimeTz(new Date(refundData.refunds.refundDate).getTime());
      })
      .catch((err) => this.helperService.showToaster('error', err))
      .finally(() => (this.isLoadingRefund = false));
  }

  /**
   * Create refund against payment
   */
  private createRefund(gpRefundObject?): void {
    this.isSubmitting = true;
    const payloadData = {
      paymentId: this.paymentId,
      payload: {
        type: this.type,
        amount: Number(this.refund.amount),
        notes: this.refund.notes,
        refundDate: this.refund.refundDateTime,
        ...gpRefundObject,
      },
    };
    this.httpService
      .createRefund(payloadData)
      .then((res: any) => {
        if (gpRefundObject) {
          this.refund = res;
          const lastRefundObject = res.refunds[res.refunds?.length - 1];
          this.refund.amount = lastRefundObject.amount;
          this.refundId = lastRefundObject._id;

          if (lastRefundObject?.globalPayResponse.transactions && lastRefundObject?.globalPayResponse?.transactions?.length) {
            const transaction = lastRefundObject.globalPayResponse.transactions[0];
            const cardDetails = transaction.credit_attributes;
            this.refund.card4Digits = this.formatService.maskCardNumber(cardDetails.card?.masked_card_number);
            this.refund.cardConfNo = cardDetails.approval_code;
          }

          this.configureTimeAndDate();
          this.title = 'Edit Refund';
          this.afterRefundOnline = true;
          this.transactionDialogue.close();
          return;
        }
        this.close();
        this.helperService.showToaster('success', `${this.refundTitle} successful`);
      })
      .catch((err) => {
        this.helperService.showToaster('error', err);
        if (gpRefundObject) {
          this.transactionDialogue.close();
        }
      })
      .finally(() => {
        this.isSubmitting = false;
        this.dataService.sendData(this.dataService.typePassData('payment-list', {}));
      });
  }

  confirm() {
    if (this.isDebitTransection() && !this.afterRefundOnline) {
      this.helperService.showToaster('error', 'Please complete refund process');
      return;
    }
    const formateDate = this.dateService.formatDate(this.payment.paymentDate, datetimeFormat.mmmdyyhhmmaa);
    const refund = '$' + this.helperService.formatAmount(Number(this.refund.amount).toFixed(2));
    const amount = '$' + this.helperService.formatAmount(Number(this.payment.amount).toFixed(2));
    const transactionMadeBy = this.payment.history[this.payment.history.length - 1].madeBy;
    const cardDigits = this.payment.paymentType === 'card' ? this.payment.card4Digits : '';
    let isOnlineCardType = '';
    if (this.payment.isOnline && this.payment.paymentType === 'card') {
      isOnlineCardType = 'Card-(Online)';
    } else if (this.payment.isCardSwipe && this.payment.paymentType === 'card') {
      isOnlineCardType = 'Card-(Swipe)';
    } else if (!this.payment.isOnline && this.payment.paymentType === 'card') {
      isOnlineCardType = 'Card-(Manual)';
    }

    const isNotOnline = this.payment.paymentType !== 'card' ? this.payment.paymentType : '';

    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-12',
        isHTML: true,
        title: `Confirm ${this.refundTitle.toLowerCase()}`,
        message: `
        <hr class="line-custome">
        <div class="row">
        <p class="pl-3">Please confirm the ${this.refundTitle.toLowerCase()} details and click proceed. </p>
        </div>
        <div class="row">
            <div class="col-10">
              <span class="font-600"><strong>Original Transaction:</strong> </span>
              <br>
              <span class="transaction-details">
              on ${isNotOnline}
              ${isOnlineCardType}
              ${cardDigits}
              by ${transactionMadeBy}, ${formateDate}</span>
            </div>
            <div class="col-2 pt-1 pl-1">
              <span> ${amount}</span>
            </div>
        </div>
        <br>
        <div class="row">
            <div class="col-10">
              <span class="font-600"><strong>${this.refundTitle} Amount:</strong> </span>
            </div>
            <div class="col-2 pl-1">
              <span class="refund-color-red"> (${refund})</span>
            </div>
        </div>
        <hr class="line-custome">`,
        button: {
          success: { title: 'Proceed' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe((confirm) => {
      if (confirm.isSuccess) {
        // this.refundWithPayRix();
        this.createRefund();
      }
      onClose.unsubscribe();
    });
  }

  /**
   * Create refund against payment
   */
  private updateRefund(): void {
    this.isSubmitting = true;
    this.httpService
      .updateRefund(this.refundId, {
        amount: Number(this.refund.amount),
        notes: this.refund.notes,
        refundDate: this.refund.refundDateTime,
      })
      .then(() => {
        this.close();
        this.helperService.showToaster('success', 'Refund updated');
      })
      .catch((err) => this.helperService.showToaster('error', err))
      .finally(() => {
        this.isSubmitting = false;
        this.dataService.sendData(this.dataService.typePassData('payment-list', {}));
      });
  }

  /**
   * Validate current form state
   */
  validateForm(): boolean {
    this.isRefundAmountFieldFocused = false;
    let isValid: boolean = true;
    if ('' + this.refund.amount === '.') {
      this.refund.amount = 0;
    }
    // if (this.payment.isPaidFull && this.postRefundAmount < 0.01 && !this.voidPayment) {
    //   this.errors.refundAmountError = 'Post refund amount must be > $0';
    //   isValid = false;
    // } else
    if (!this.voidPayment && this.refund.amount > this.remainingPaymentBalance) {
      this.errors.refundAmountError = 'Refund amount must be less than or equal to the gross paid amount.';
      isValid = false;
    } else if (!this.refund.amount || (this.refund.amount == 0 && !this.voidPayment)) {
      this.errors.refundAmountError = 'Refund value is required';
      isValid = false;
    }
    return isValid;
  }

  /**
   * On refund amount change
   */
  handleRefundAmountChange(amount: number): void {
    this.refund.amount = amount;
  }

  /**
   * Refund amount field focus
   */
  handleRefundAmountFocus(): void {
    this.errors.refundAmountError = '';
    this.isRefundAmountFieldFocused = true;
  }

  /**
   * Handle submit button press
   */
  onSubmit() {
    if (this.isDebitTransection() && !this.afterRefundOnline) {
      this.helperService.showToaster('error', 'Please complete refund process');
      return;
    }
    if (!this.validateForm()) {
      return;
    }

    // merge seperate created date time into single date
    const date = this.dateService.formatDate(this.refund.refundDate, datetimeFormat.yyymmdd);
    const time = this.dateService.formatDate(this.refund.refundTime, datetimeFormat.hhmm);
    this.refund.refundDateTime = `${date} ${time}`;

    // if refund id does not exists that means we are creating a refund
    if (!this.refundId) {
      this.featureIsPayRixEnable ? this.confirm() : this.createRefund();
    } else {
      this.updateRefund();
    }
  }

  /**
   * Close modal
   *
   * @param data - Any data to pass back
   */
  close(data?: any): void {
    this.ref.close(data);
  }

  // GP configurations

  getTerminals() {
    this.httpService.globalPaymentGetTerminalList().then((r) => {
      this.terminalList = r
        .map((e) => {
          // if (e.isTransactionPending) {
          //   return;
          // }
          return {
            name: e.deviceName,
            deviceId: e.deviceId,
            id: e._id,
            isTransactionPending: e.isTransactionPending ? true : false,
            pendingRo: e.pendingRo ? true : false,
            disabled: e.isTransactionPending ? true : false,
          };
        })
        .filter((x) => x);
    });
  }

  onTerminalSelect($event: any) {
    this.selectedTerminal = $event;
  }

  openCardSwipe() {
    if (!this.refund.amount || !this.selectedTerminal) return;
    this.opening = true;
    this.transactionDialogue = this.dialogService.open(SwipeCardConfirmationComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        title: 'Refund',
        isGlobalPayment: true,
        styleClassHeading: 'font-14',
        terminal: this.selectedTerminal,
        isProcessing: this.selectedTerminal.isTransactionPending,
        amount: this.refund.amount,
        id: this.order.estimateId,
        isRefund: true,
      },
    });

    this.transactionDialogue.onClose.subscribe((res) => {
      this.opening = false;
      if (res && res.isSuccess !== undefined && res.isSuccess) {
        this.createGlopabPaySwipeTransaction();
      }
    });
  }

  createGlopabPaySwipeTransaction() {
    const payload = {
      paymentProvider: 'globalPay',
      terminalId: this.selectedTerminal.id,
      refundCardSwipe: true,
    };
    this.showProgressModal();
    const date = this.dateService.formatDate(this.refund.refundDate, datetimeFormat.yyymmdd);
    const time = this.dateService.formatDate(this.refund.refundTime, datetimeFormat.hhmm);
    this.refund.refundDateTime = `${date} ${time}`;
    this.createRefund(payload);
  }

  showProgressModal() {
    this.transactionDialogue = this.dialogService.open(SwipeCardConfirmationComponent, {
      width: '30%',
      closeOnEscape: false,
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        title: 'Refund',
        isRefund: true,
        styleClassHeading: 'font-14',
        isProcessing: true,
        amount: this.isSwipeRequestPending ? this.swapAmount : this.refund.amount,
        id: this.order.estimateId,
        isGlobalPayment: true,
      },
    });
  }

  openScreen() {
    this.refund.collectedAmount = this.refund.amount;
    const payload = {
      ...this.refund,
      customer: this.payment.customer,
      code: this.payment.code,
      estimate: this.order,
      refundOnline: true,
      type: this.type,
      amount: Number(this.refund.amount),
      id: this.payment.id,
    };
    const ref = this.dialogService.open(GlobalPaymentFieldComponent, {
      width: '55%',
      styleClass: 'payrix-dialog',
      showHeader: false,
      data: {
        payment: payload,
        credentials: this.credentials,
        orderVehicle: {},
        // globalPaymentMehod: this.globalPaymentMehod,
        // activePaymentValue: this.activePayment.value,
        paymentTime: this.paymentTime,
        isRefund: true,
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        // this.afterOnlinePayment = true;
        // this.showNotes = true;
        // this.paymentId = data._id;
        this.getRefundDetails();
        // this.getPaymentHistory(data.estimate.estimateId);
        // this.getPaymentDetails();
        // this.cdr.detectChanges();

        this.getPaymentDetails();
      }
      onClose.unsubscribe();
    });
  }

  selectPaymentSource(paymentSource: string) {
    if (paymentSource === 'isSourcePaymentOnline') {
      this.isSourcePaymentOnline = true;
      this.isSourcePaymentCardSwipe = false;
    } else if (paymentSource === 'isSourcePaymentCardSwipe') {
      this.isSourcePaymentOnline = false;
      this.isSourcePaymentCardSwipe = true;
    }
  }

  getGlobalPaymentCredentials() {
    this.httpService.getGlobalPaymentInfo().then((data: any) => {
      if (data) {
        const { hash } = data;

        if (hash !== '') {
          this.credentials = this.helperService.decryptPaymentData(hash);
        }
      }
    });
  }

  async checkSwapPendingService(id) {
    const data = await this.httpService.isSwipeRequestPending(id);
    if (data) {
      this.isSwipeRequestPending = data && data.isSwipeRefundRequestPending;
      if (this.isSwipeRequestPending) {
        this.swapAmount = data.swipeAmt;

        this.ref.close();
        this.showProgressModal();
      }
    }
  }

  isDebitTransection() {
    if (this.isTransectionStatusDebit && this.refundStatus == 2) {
      return true;
    } else {
      return false;
    }
  }
}
